import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenav } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SignInService } from '../sign-in/sign-in.service';
import { NavigationService } from './navigation.service';
import { BreadcrumbItem } from '../common/component/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../common/component/breadcrumb/breadcrumb.service';
import {
  DEMO_CURRENT_WEATHER,
  DEMO_FORECAST_KINDS,
  WeatherInfo,
  weatherInfoFromKind
} from './weather.model';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('sideNav') sideNav: MatSidenav | undefined;
  title: string = '';
  navigation: NavigationService | undefined;
  sideNavMode: string = 'side';

  /** 로그인 등에서는 상단 바 숨김 */
  showTopNav = false;

  /**
   * 테마 선택값. '1' = GitHub Dark, '6' = GitHub Light.
   *
   * 전환은 CSS(html:has(#theme-N:checked))가 하지만 라디오는 새로고침하면
   * 마크업의 기본값으로 되돌아간다. localStorage 에 담아 두고 최초 렌더에서
   * [checked] 로 바로 복원한다 — ngOnInit 에서 바꾸면 다크로 한 번 그렸다가
   * 라이트로 넘어가며 깜빡인다.
   */
  private readonly THEME_KEY = 'app-theme';
  theme: string = this.readTheme();

  /**
   * 사이드 서브메뉴 전체 사용 여부.
   *
   * 하위 화면 이동은 상단 메가메뉴가 모두 커버하므로(access/visit/user/admin 전 경로
   * 링크 존재) 사이드바는 쓰지 않기로 했다. 마크업·스타일·서브메뉴 정의는 그대로
   * 남겨 두었으니, 되살리려면 이 값만 true 로 바꾸면 된다.
   */
  private readonly SIDE_NAV_ENABLED = false;

  /**
   * 현재 화면에서 사이드 서브메뉴를 렌더할지.
   * false 면 사이드바와 여는 햄버거 버튼을 아예 렌더하지 않는다.
   */
  hasSideNav = false;

  /**
   * 대시보드(/main) 여부.
   * 대시보드는 그 자체가 홈이라 breadcrumb 이 가리킬 상위가 없고,
   * 카드로 화면을 꽉 채우므로 footer 도 뺀다.
   */
  isDashboard = false;
  currentDate = new Date();
  currentWeather: WeatherInfo = DEMO_CURRENT_WEATHER;
  readonly forecastDayOffsets = [1, 2, 3, 4, 5];
  readonly weatherRainDrops = [
    { left: 8, delay: 0, duration: 1.1 },
    { left: 24, delay: 0.35, duration: 1.25 },
    { left: 42, delay: 0.15, duration: 1.05 },
    { left: 58, delay: 0.55, duration: 1.2 },
    { left: 74, delay: 0.25, duration: 1.15 },
    { left: 88, delay: 0.45, duration: 1.3 }
  ];


  breadcrumbItems$: Observable<BreadcrumbItem[]>;

  /** 메뉴 클릭 이동 후 hover·focus-within 으로 메가 패널이 다시 열리지 않도록 */
  megaMenuSuppressed = false;

  private clockSub?: Subscription;
  private routerSub?: Subscription;

  constructor(
    private router: Router,
    private navigationService: NavigationService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private signIn: SignInService,
    private breadcrumb: BreadcrumbService
  ) {
    this.navigation = navigationService;
    this.breadcrumbItems$ = this.breadcrumb.items$;

    iconRegistry.addSvgIcon(
      'account_tree', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/material-icons/account_tree.svg'));
    iconRegistry.addSvgIcon(
      'add_moderator', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/material-icons/add_moderator.svg'));
    iconRegistry.addSvgIcon(
      'logout', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/material-icons/logout.svg'));
  }

  ngOnInit(): void {
    this.syncTopNavVisibility(this.router.url);
    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe((e) => this.syncTopNavVisibility(e.urlAfterRedirects));

    this.clockSub = interval(1000).subscribe(() => {
      this.currentDate = new Date();
    });
  }

  private syncTopNavVisibility(url: string): void {
    const path = url.split('?')[0];
    this.showTopNav = !path.startsWith('/signin');
    this.isDashboard = path === '/main' || path.startsWith('/main/');
    this.syncSideNavDefault(path);
  }

  /**
   * 사이드 서브메뉴 기본 상태 — 서브메뉴가 있는 구역이면 펼치고, 대시보드처럼
   * 서브메뉴가 없는 화면에서는 접는다.
   *
   * 서브메뉴 블록은 user / visit / access / admin 4개 구역에만 있어서,
   * "대시보드 빼고 무조건 열기" 로 하면 /device · /my-info 에서 빈 사이드바가 뜬다.
   *
   * 열림/닫힘은 CSS 체크박스(#app-shell-nav-toggle)가 제어하므로 checked 를 직접
   * 세팅한다. 라우트가 바뀔 때만 기본값으로 되돌리므로, 같은 화면에 머무는 동안
   * 햄버거로 접어둔 사용자의 선택은 유지된다.
   * 체크박스가 *ngIf 로 렌더되어 이 시점에 아직 없을 수 있어 렌더 이후로 미룬다.
   */
  private syncSideNavDefault(path: string): void {
    const shouldOpen = this.showTopNav && this.hasSideMenu(path);
    this.hasSideNav = shouldOpen;
    setTimeout(() => {
      const toggle = document.getElementById('app-shell-nav-toggle') as HTMLInputElement | null;
      if (toggle) { toggle.checked = shouldOpen; }
    });
  }

  private hasSideMenu(path: string): boolean {
    if (!this.SIDE_NAV_ENABLED) { return false; }
    return ['/user', '/visit', '/access', '/admin']
      .some((base) => path === base || path.startsWith(base + '/'));
  }

  ngAfterViewInit(): void {
    this.navigation?.setSideNav(this.sideNav);
  }

  private readTheme(): string {
    try {
      return localStorage.getItem(this.THEME_KEY) || '1';
    } catch {
      return '1';   // 사생활 보호 모드 등 localStorage 차단 환경
    }
  }

  onThemeChange(value: string): void {
    this.theme = value;
    try {
      localStorage.setItem(this.THEME_KEY, value);
    } catch {
      // 저장이 막혀도 이번 세션 전환은 그대로 동작한다.
    }
  }

  ngOnDestroy(): void {
    this.clockSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  toggle(): void {
    // this.navigation?.toggle();
  }

  back(): void {
    this.navigation?.back();
  }

  signOut(): void {
    this.signIn.signOut();
  }

  setTitle(title: string): void {
    this.title = title;
  }

  goToLink(url: string, subTabId?: string): void {
    if (!url || url === '#') {
      return;
    }
    this.closeMenus();
    const path = url.split('?')[0].split('#')[0];
    const current = this.router.url.split('?')[0].split('#')[0];
    const navigate = current === path
      ? Promise.resolve(true)
      : this.router.navigateByUrl(url, { replaceUrl: false });

    navigate.then(() => {
      if (subTabId) {
        this.activateSubTab(subTabId);
      }
    });
  }

  private activateSubTab(id: string, attempt = 0): void {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el?.type === 'radio') {
      el.checked = true;
      return;
    }
    if (attempt < 30) {
      setTimeout(() => this.activateSubTab(id, attempt + 1), 50);
    }
  }

  onMegaMenuMouseLeave(): void {
    this.megaMenuSuppressed = false;
  }

  private closeMenus(): void {
    this.megaMenuSuppressed = true;
    const active = document.activeElement as HTMLElement | null;
    active?.blur?.();
    // 사이드 서브메뉴는 여기서 강제로 닫지 않는다. 이동 후 NavigationEnd 에서
    // syncSideNavDefault 가 해당 화면의 기본 상태로 정리한다 (닫았다 여는 깜빡임 방지).
  }

  showMenu(url: string): boolean {
    return !!this.navigation?.menu?.some((item) => item.url === url);
  }

  isNavActive(url: string): boolean {
    const current = this.router.url.split('?')[0];
    if (url === '/user') {
      return current === '/user' || current.startsWith('/user/');
    }
    if (url === '/admin') {
      return current === '/admin' || current.startsWith('/admin/');
    }
     if (url === '/visit') {
      return current.startsWith('/visit');
     }
    // user 하위는 상세/자식 라우트(예: /user/management/user-detail)도 활성 처리
    if (url === '/user/organization' || url === '/user/management') {
      return current === url || current.startsWith(url + '/');
    }
    if (url === '/access') {
      return current.startsWith('/access');
    }
    return current === url;
  }

  isUserSection(): boolean {
    const current = this.router.url.split('?')[0].split('#')[0];
    return current === '/user' || current.startsWith('/user/');
  }

  isAdminSection(): boolean {
    const current = this.router.url.split('?')[0].split('#')[0];
    return current === '/admin' || current.startsWith('/admin/');
  }

  getForecastWeather(dayOffset: number): WeatherInfo {
    const kind = DEMO_FORECAST_KINDS[dayOffset - 1] ?? 'cloudy';
    return weatherInfoFromKind(kind);
  }
}
